/**
 * Review notes for chemical compatibility screening results at a storage location.
 *
 * A review is tied to the exact inputs that were screened: the rules version, the waste streams, and the equipment.
 * When any of those change, the review no longer counts toward the current assessment, but its note is still
 * surfaced so the reviewer can carry it forward.
 */

import checker from '../data/compatibility-checker.json' with { type: 'json' };
import type {
  CompatibilityResolutionRecord,
  FacilityStorageVessel,
  FacilityStorageVesselID,
  HazWasteStream,
  HazWasteStreamID,
  SimpleSelectable,
} from '../../tri-shared/types/db.ts';

export type CompatibilityDecision = 'compatible' | 'conditional' | 'incompatible' | 'unknown';

export type CompatibilityReviewStream = Pick<
  SimpleSelectable<HazWasteStream>,
  'hazWasteStreamID' | 'name' | 'dotShippingDescription' | 'epaWasteCodes' | 'physicalState' | 'updatedAt'
>;

export type CompatibilityReviewVessel = Pick<
  SimpleSelectable<FacilityStorageVessel>,
  | 'facilityStorageVesselID'
  | 'updatedAt'
  | 'storageTypeID'
  | 'vesselMaterialConstruction'
  | 'facilityStorageLocationID'
  | 'hazWasteStreamID'
  | 'hasSecondaryContainment'
  | 'secondaryContainmentType'
  | 'equipmentDetails'
>;

export const COMPATIBILITY_RULES_VERSION = checker.dataset_version + ':' + checker.updated + ':screening-2';

/** Review identities include assessed inputs and rule/algorithm versions.
 * Legacy ID-only reviews deliberately do not match; their notes remain available.
 * JSON avoids hash collisions and is stored only as an opaque key, never displayed.
 */
export function compatibilityReviewKey(
  subject: string,
  streams: CompatibilityReviewStream[],
  equipment: CompatibilityReviewEquipment,
  rulesVersion: string,
): string {
  return JSON.stringify([
    subject,
    rulesVersion,
    [...streams]
      .sort((a, b) => a.hazWasteStreamID - b.hazWasteStreamID)
      .map((stream) => [
        stream.hazWasteStreamID,
        stream.updatedAt,
        stream.name,
        stream.dotShippingDescription,
        [...stream.epaWasteCodes].sort(),
        stream.physicalState,
      ]),
    equipment,
  ]);
}

/** A pair review covers every vessel at the location, so any equipment change there requires a new review. */
export function compatibilityReviewKeyForPair(
  idA: HazWasteStreamID,
  idB: HazWasteStreamID,
  streams: CompatibilityReviewStream[],
  vessels: CompatibilityReviewVessel[],
): string {
  const ids = [idA, idB].sort((a, b) => a - b);
  return compatibilityReviewKey(
    ids.join('-'),
    streams.filter((stream) => ids.includes(stream.hazWasteStreamID)),
    reviewEquipment(vessels, null),
    COMPATIBILITY_RULES_VERSION,
  );
}

/** A vessel review covers only that vessel and the stream it holds. */
export function compatibilityReviewKeyForVessel(
  vesselID: FacilityStorageVesselID,
  streams: CompatibilityReviewStream[],
  vessels: CompatibilityReviewVessel[],
): string {
  const vessel = vessels.find((item) => item.facilityStorageVesselID === vesselID);
  const streamID = vessel ? vessel.hazWasteStreamID : null;
  return compatibilityReviewKey(
    `vessel-${vesselID}`,
    streams.filter((stream) => stream.hazWasteStreamID === streamID),
    reviewEquipment(vessels, vesselID),
    COMPATIBILITY_RULES_VERSION,
  );
}

type CompatibilityReviewEquipment = ReturnType<typeof reviewEquipment>;

function reviewEquipment(vessels: CompatibilityReviewVessel[], vesselID: FacilityStorageVesselID | null) {
  return vessels
    .filter((vessel) => vesselID == null || vessel.facilityStorageVesselID === vesselID)
    .sort((a, b) => a.facilityStorageVesselID - b.facilityStorageVesselID)
    .map((vessel) => [
      vessel.facilityStorageVesselID,
      vessel.updatedAt,
      vessel.storageTypeID,
      vessel.vesselMaterialConstruction,
      vessel.facilityStorageLocationID,
      vessel.hazWasteStreamID,
      vessel.hasSecondaryContainment,
      vessel.secondaryContainmentType,
      vessel.equipmentDetails,
    ]);
}

export interface CompatibilityReviewInputs {
  pairs: { streamIdA: HazWasteStreamID; streamIdB: HazWasteStreamID; decision: CompatibilityDecision }[];
  materials: { vesselID: FacilityStorageVesselID; decision: CompatibilityDecision }[];
  streams: CompatibilityReviewStream[];
  vessels: CompatibilityReviewVessel[];
  resolutions: CompatibilityResolutionRecord[];
}

/**
 * - `concerns`: an unreviewed result is incompatible.
 * - `not-evaluated`: an unreviewed result is neither compatible nor incompatible.
 * - `reviewed`: every result that needed a review has a current one.
 * - `no-concerns`: every result was compatible, so no review was needed.
 */
export type CompatibilityReviewStatus = 'concerns' | 'not-evaluated' | 'reviewed' | 'no-concerns';

export function compatibilityReviewStatus(inputs: CompatibilityReviewInputs): CompatibilityReviewStatus {
  const reviewedKeys = new Set(inputs.resolutions.map((resolution) => resolution.pairKey));
  const needsReview = [
    ...inputs.materials
      .filter((material) => material.decision !== 'compatible')
      .map((material) => ({
        decision: material.decision,
        key: compatibilityReviewKeyForVessel(material.vesselID, inputs.streams, inputs.vessels),
      })),
    ...inputs.pairs.map((pair) => ({
      decision: pair.decision,
      key: compatibilityReviewKeyForPair(pair.streamIdA, pair.streamIdB, inputs.streams, inputs.vessels),
    })),
  ];
  const open = needsReview.filter((item) => !reviewedKeys.has(item.key));
  if (open.some((item) => item.decision === 'incompatible')) return 'concerns';
  if (open.length) return 'not-evaluated';
  if (needsReview.length) return 'reviewed';
  return 'no-concerns';
}

/** Reviews whose inputs no longer match any current pair or vessel. They do not count as current reviews. */
export function compatibilityReviewsStale(
  inputs: Pick<CompatibilityReviewInputs, 'pairs' | 'streams' | 'vessels' | 'resolutions'>,
): CompatibilityResolutionRecord[] {
  const currentKeys = new Set([
    ...inputs.pairs.map((pair) =>
      compatibilityReviewKeyForPair(pair.streamIdA, pair.streamIdB, inputs.streams, inputs.vessels),
    ),
    ...inputs.vessels.map((vessel) =>
      compatibilityReviewKeyForVessel(vessel.facilityStorageVesselID, inputs.streams, inputs.vessels),
    ),
  ]);
  return inputs.resolutions.filter((review) => !currentKeys.has(review.pairKey));
}

/** The most recent note for the same pair or vessel, including legacy ID-only keys, to pre-fill a new review. */
export function compatibilityReviewPreviousNote(key: string, resolutions: CompatibilityResolutionRecord[]): string {
  const subject = JSON.parse(key)[0];
  return (
    [...resolutions].reverse().find((review) => {
      if (review.pairKey === subject) return true;
      try {
        return JSON.parse(review.pairKey)[0] === subject;
      } catch {
        return false;
      }
    })?.note ?? ''
  );
}
